/**
 * useBatchAutoPolling - Automatically polls batch status in background
 *
 * This hook handles automatic polling of batch jobs when they are in
 * "submitted" or "running" state. It triggers the actual poll action
 * (POST /api/v1/batch/poll/<run_id>) which fetches updates from Google API.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { pollBatch } from '@/lib/api/batch';

interface AutoPollingOptions {
  runId: string;
  status: string | undefined;
  enabled?: boolean;
  interval?: number; // milliseconds
}

export function useBatchAutoPolling({
  runId,
  status,
  enabled = true,
  interval = 15000 // 15 seconds default
}: AutoPollingOptions) {
  const queryClient = useQueryClient();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastPollRef = useRef<number>(0);

  const pollMutation = useMutation({
    mutationFn: () => pollBatch(runId),
    onSuccess: (result) => {
      console.log(`[Auto-Poll] Batch ${runId.slice(0, 8)}... updated:`, {
        status: result.status,
        completed: result.completed_items,
        failed: result.failed_items
      });

      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['batch', 'status', runId] });
      queryClient.invalidateQueries({ queryKey: ['batch', 'images', runId] });
      queryClient.invalidateQueries({ queryKey: ['batch', 'runs'] });
    },
    onError: (err) => {
      console.error(`[Auto-Poll] Failed for batch ${runId.slice(0, 8)}...:`, err);
    },
  });

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only auto-poll if enabled and status is submitted/running
    const shouldPoll = enabled &&
                      status &&
                      ['submitted', 'running'].includes(status);

    if (!shouldPoll) {
      return;
    }

    console.log(`[Auto-Poll] Starting for batch ${runId.slice(0, 8)}... (${status})`);

    // Poll immediately on first load (if not polled in last 10 seconds)
    const now = Date.now();
    if (now - lastPollRef.current > 10000) {
      pollMutation.mutate();
      lastPollRef.current = now;
    }

    // Set up recurring poll
    intervalRef.current = setInterval(() => {
      if (!pollMutation.isPending) {
        console.log(`[Auto-Poll] Polling batch ${runId.slice(0, 8)}...`);
        pollMutation.mutate();
        lastPollRef.current = Date.now();
      }
    }, interval);

    // Cleanup on unmount or status change
    return () => {
      if (intervalRef.current) {
        console.log(`[Auto-Poll] Stopping for batch ${runId.slice(0, 8)}...`);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [runId, status, enabled, interval, pollMutation]);

  return {
    isPolling: pollMutation.isPending,
    lastPollRef,
  };
}
